import { observableValue, derived, type IObservable, type IReader } from '@vscode/observables';
import type { FixtureExport } from './fixtureApi.js';
import { type ComponentDefinition } from './fixtureApiConsumer.js';
import { createFixtureTree, findNodeByPath, type FixtureNode } from './FixtureNode.js';

export class FixtureRegistry {
  private readonly _fixtures = observableValue<Map<string, FixtureExport>>(this, new Map());
  readonly loading = observableValue<boolean>(this, false);
  readonly error = observableValue<string | undefined>(this, undefined);

  readonly root: IObservable<FixtureNode> = derived(this, (reader) => {
    const fixtures = this._fixtures.read(reader);
    return createFixtureTree(fixtures);
  });

  register(path: string, fixtureExport: FixtureExport): void {
    const fixtures = new Map(this._fixtures.get());
    fixtures.set(path, fixtureExport);
    this._fixtures.set(fixtures, undefined);
  }

  unregister(path: string): void {
    const fixtures = new Map(this._fixtures.get());
    if (fixtures.delete(path)) {
      this._fixtures.set(fixtures, undefined);
    }
  }

  clear(): void {
    this._fixtures.set(new Map(), undefined);
  }

  getNode(reader: IReader, path: string[]): FixtureNode | undefined {
    const root = this.root.read(reader);
    return findNodeByPath(root, path);
  }

  getComponentByPath(path: string[]): ComponentDefinition | undefined {
    const fixtures = this._fixtures.get();
    const tree = createFixtureTree(fixtures);
    const node = findNodeByPath(tree, path);
    return node?.component;
  }
}
